import { deviceFontsMap } from '@/resources/text-fonts';
import { ElectronApi } from '@/services/electron.api';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { EventType, emitter } from '@/utils/emitter.utils';
import { pickFile } from '@/utils/fs/pick-file.utils';
import { computed } from 'vue';
import { RemoteLibsService } from '@/services/remote-libs.service';
import { WatchfaceService } from '@/services/watchface';
import { WatchfaceDeviceType } from './use-watchface-devices';

export const useTextFont = () => {
  const { deviceType } = useWatchfaceStore();

  const getFontIdList = (deviceType: WatchfaceDeviceType) => {
    return deviceFontsMap[deviceType] ?? deviceFontsMap['N62'] ?? [];
  };

  // 默认使用N62设备的
  const fontIdList = computed(() => getFontIdList(deviceType));

  const _loadReomteFont = (
    fontId: string,
    fontWeight: string,
    fontFormat: string
  ) => {
    if (!fontId || !fontWeight) return;
    const fontStyle = `${fontId}-${fontWeight}`;
    const fontFace = new window.FontFace(
      `${fontStyle}`,
      `url(https://cdn.cnbj1.fds.api.mi-img.com/watchface-editor-release/fonts/${fontStyle}.${fontFormat})`
    );
    // eslint-disable-next-line
    // @ts-ignore
    document.fonts?.add?.(fontFace);
    return fontFace.load();
  };

  const _loadLocalFont = async (fontId: string, fontFile: string) => {
    if (!fontId || !fontFile) return;
    // 本地字重均为noraml
    const fontStyle = `${fontId}-normal`;
    const fs = ElectronApi.fs;
    if (!fs) return;

    const buffer = await fs.promises.readFile(fontFile);
    const fontFace = new window.FontFace(`${fontStyle}`, buffer);
    // eslint-disable-next-line
    // @ts-ignore
    document.fonts?.add?.(fontFace);
    await fontFace.load();
  };

  const loadFontFile = (data: {
    remote?: {
      fontId: string;
      fontWeight: string;
      fontFormat: string;
    };
    local?: {
      fontId: string;
      fontFile: string;
    };
  }) => {
    const { remote, local } = data;
    const p1 = remote
      ? _loadReomteFont(
          remote.fontId,
          remote.fontWeight,
          remote.fontFormat.toLowerCase()
        )
      : Promise.resolve();

    const p2 = local
      ? _loadLocalFont(local.fontId, local.fontFile)
      : Promise.resolve();

    Promise.all([p1, p2])
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        emitter.emit(EventType['watchface:font-loaded']);
      });
  };

  const _getRemoteFonts = () => {
    const fonts: {
      fontid: string;
      weight: string;
      format: string;
    }[] = [];
    Object.values(deviceFontsMap).forEach((item) => {
      item.forEach((item2) => {
        const { format, value: fontid, weights } = item2;
        weights.forEach((item3) => {
          const weight = item3.value;
          if (
            fonts.find(
              (font) =>
                font.weight == weight &&
                font.fontid == fontid &&
                font.format == format
            )
          )
            return;
          fonts.push({ fontid, format, weight });
        });
      });
    });

    return fonts;
  };

  const loadAllFonts = (
    localFonts?: { fontId: string; fontFile: string }[]
  ) => {
    const remoteFonts = _getRemoteFonts();
    remoteFonts.forEach((font) => {
      loadFontFile({
        remote: {
          fontId: font.fontid,
          fontWeight: font.weight,
          fontFormat: font.format
        }
      });
    });
    localFonts?.forEach((font) => {
      loadFontFile({
        local: {
          fontId: font.fontId,
          fontFile: font.fontFile
        }
      });
    });
  };

  const pickThenUploadFont = async () => {
    const { fs, path } = ElectronApi;
    if (!fs || !path) return;

    const file = await pickFile({
      extensions: ['ttf'],
      multi: false
    });
    const fontPath = file?.[0];
    if (!file || !fontPath) return;

    // 将字体文件复制到表盘目录下
    const service = new WatchfaceService();
    const fontFilename = await service.resource.addFile(fontPath, {
      prefix: 'font_'
    });
    return {
      fontFilename,
      fontBaseName: path.basename(fontPath)
    };
  };

  const clipFont = async (
    fontPath: string,
    clipFontPath: string,
    text: string
  ) => {
    const service = new RemoteLibsService();
    const fontClip = await service.getLibFilename('fontClip');
    if (!fontClip) return;
    await ElectronApi.execFile(fontClip, [
      '-p',
      fontPath,
      '-f',
      clipFontPath,
      '-t',
      text
    ]);
    return clipFontPath;
  };

  return {
    fontIdList,
    loadFontFile,
    loadAllFonts,
    pickThenUploadFont,
    clipFont,
    getFontIdList
  };
};
